import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHead, Modal, Meter } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, useStore, getState, money, STUDIO } from "../lib/store";
import { useIsAdmin, useSession } from "../lib/auth";
import { knowledgeAsk } from "../lib/ai";
import { notify } from "../lib/notify";
import { SAVED_VIEWS, feeErosion } from "../lib/views";
import { contactBook, stageHistory, forecastStage } from "../lib/contacts";
import { printTable } from "../lib/printView";
import { fmtDate } from "../lib/format";
import type { Project } from "../lib/types";

/* Studio-wide tools that sit above any one project: who's loaded, the studio's
   institutional memory, saved project templates, and a workspace backup. */
export default function StudioTools() {
  const [tab, setTab] = useState<"views" | "erosion" | "capacity" | "timing" | "contacts" | "comms" | "knowledge" | "templates" | "backup">("views");
  const TABS = [
    { key: "views", label: "Views" }, { key: "erosion", label: "Fee risk" }, { key: "capacity", label: "Capacity" },
    { key: "timing", label: "Timing" }, { key: "contacts", label: "Contacts" },
    { key: "comms", label: "Bulk message" }, { key: "knowledge", label: "Knowledge base" },
    { key: "templates", label: "Templates" }, { key: "backup", label: "Backup" },
  ] as const;
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "34px clamp(18px,4vw,44px) 80px" }}>
      <PageHead kicker="Studio" title="Studio tools" />
      <div className="row wrap reveal" style={{ gap: 6, marginBottom: 22 }}>
        {TABS.map((t) => (
          <button key={t.key} className={"chip" + (tab === t.key ? " on" : "")} style={{ fontSize: 12.5, background: tab === t.key ? "var(--accent)" : "var(--card)", color: tab === t.key ? "#fff" : "var(--ink-2)", borderColor: tab === t.key ? "var(--accent)" : "var(--line-2)" }} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {tab === "views" && <Views />}
      {tab === "erosion" && <FeeRisk />}
      {tab === "capacity" && <Capacity />}
      {tab === "timing" && <Timing />}
      {tab === "contacts" && <Contacts />}
      {tab === "comms" && <BulkMessage />}
      {tab === "knowledge" && <KnowledgeBase />}
      {tab === "templates" && <Templates />}
      {tab === "backup" && <Backup />}
    </div>
  );
}

/* ---- saved cross-project views ---- */
function Views() {
  const { projects } = useStore();
  const session = useSession();
  const nav = useNavigate();
  const me = session?.name ?? STUDIO.principal;
  const [key, setKey] = useState(SAVED_VIEWS[0].key);
  const view = SAVED_VIEWS.find((v) => v.key === key)!;
  const rows = view.rows(projects, me);

  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted reveal" style={{ maxWidth: 640 }}>
        The questions you ask every day, answered across every project at once.
      </p>
      <div className="row wrap reveal" style={{ gap: 6 }}>
        {SAVED_VIEWS.map((v) => {
          const n = v.rows(projects, me).length;
          return (
            <button key={v.key} className={"chip" + (key === v.key ? " on" : "")} title={v.blurb}
              style={{ fontSize: 12, background: key === v.key ? "var(--accent)" : "var(--card)", color: key === v.key ? "#fff" : "var(--ink-2)", borderColor: key === v.key ? "var(--accent)" : "var(--line-2)" }}
              onClick={() => setKey(v.key)}>{v.label}{n ? ` · ${n}` : ""}</button>
          );
        })}
      </div>
      <div className="row between wrap reveal" style={{ gap: 10 }}>
        <span className="dim" style={{ fontSize: 12.5 }}>{view.blurb} — {rows.length} item{rows.length === 1 ? "" : "s"}</span>
        {rows.length > 0 && (
          <button className="btn ghost sm" onClick={() => printTable({
            title: view.label, subtitle: view.blurb, meta: [`${rows.length} items`, new Date().toLocaleDateString()],
            columns: [{ key: "project", label: "Project" }, { key: "ref", label: "Ref", width: "70px" }, { key: "title", label: "Item" }, { key: "meta", label: "Detail" }],
            rows: rows as any,
          })}><Icon.report width={13} />Print</button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="panel reveal" style={{ padding: 26, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>Nothing here — all clear.</div>
      ) : (
        <div className="card reveal" style={{ overflow: "hidden" }}>
          {rows.map((r, i) => (
            <button key={`${r.pid}-${r.ref}-${i}`} className="row between wrap" onClick={() => nav(`/studio/${r.pid}/${r.section}`)}
              style={{ width: "100%", textAlign: "left", padding: "11px 16px", borderTop: i ? "1px solid var(--line)" : "none", border: "none", background: "transparent", cursor: "pointer", gap: 10 }}>
              <span className="row" style={{ gap: 10, minWidth: 0 }}>
                <span style={{ width: 7, height: 7, borderRadius: 7, background: r.urgent ? "var(--danger)" : "var(--ink-3)", flexShrink: 0 }} />
                <span className="mono dim" style={{ fontSize: 11 }}>{r.ref}</span>
                <span style={{ fontSize: 13.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
              </span>
              <span className="row" style={{ gap: 10, flexShrink: 0 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>{r.meta}</span>
                <span className="badge" style={{ fontSize: 8.5 }}>{r.project}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- which projects are quietly losing money ---- */
const RISK_HUE: Record<string, string> = { high: "var(--danger)", medium: "var(--warn)", low: "var(--ok)" };
function FeeRisk() {
  const { projects } = useStore();
  const nav = useNavigate();
  const rows = feeErosion(projects);
  const atRisk = rows.filter((r) => r.risk !== "low");
  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted reveal" style={{ maxWidth: 660 }}>
        Where the fee is eroding — work running ahead of billing, approved variations left uninvoiced, extras flagged but never raised, heavy rework. Signals from your own record, not a prediction to take on faith.
      </p>
      <div className="row between wrap reveal" style={{ gap: 10 }}>
        <span className="dim" style={{ fontSize: 12.5 }}>{atRisk.length} of {rows.length} project{rows.length === 1 ? "" : "s"} worth a look</span>
        {rows.length > 0 && (
          <button className="btn ghost sm" onClick={() => printTable({
            title: "Fee risk review", subtitle: "Projects where the fee may be eroding", meta: [new Date().toLocaleDateString()],
            columns: [{ key: "name", label: "Project" }, { key: "risk", label: "Risk", width: "60px" }, { key: "progressPct", label: "Progress %", align: "right", width: "70px" }, { key: "billedPct", label: "Billed %", align: "right", width: "70px" }, { key: "why", label: "Why" }],
            rows: rows.map((r) => ({ name: `${r.name} (${r.code})`, risk: r.risk, progressPct: r.progressPct, billedPct: r.billedPct, why: r.reasons.join("; ") || "—" })),
          })}><Icon.report width={13} />Print</button>
        )}
      </div>
      <div className="col" style={{ gap: 10 }}>
        {rows.map((r) => (
          <div key={r.pid} className="card pad reveal" style={{ borderLeft: `3px solid ${RISK_HUE[r.risk]}` }}>
            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="row" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>{r.name}</span>
                <span className="mono dim" style={{ fontSize: 11 }}>{r.code}</span>
                <span className="badge" style={{ fontSize: 8.5, color: RISK_HUE[r.risk] }}>{r.risk} risk</span>
              </span>
              <button className="btn ghost sm" onClick={() => nav(`/studio/${r.pid}/billing`)}>Open billing</button>
            </div>
            <div className="row wrap" style={{ gap: 18, marginTop: 10, alignItems: "center" }}>
              <div style={{ flex: "1 1 220px", minWidth: 180 }}>
                <div className="row between" style={{ fontSize: 11.5, marginBottom: 3 }}><span className="dim">Work done</span><span className="mono">{r.progressPct}%</span></div>
                <Meter value={r.progressPct} />
                <div className="row between" style={{ fontSize: 11.5, margin: "7px 0 3px" }}><span className="dim">Fee invoiced</span><span className="mono">{r.billedPct}%</span></div>
                <Meter value={r.billedPct} color={r.gap >= 15 ? "var(--warn)" : undefined} />
              </div>
              {r.unbilledExtras > 0 && <div><div className="kicker" style={{ fontSize: 8.5 }}>Uninvoiced variations</div><div className="serif" style={{ fontSize: 20, marginTop: 2 }}>{money(r.unbilledExtras, r.currency)}</div></div>}
            </div>
            {r.reasons.length > 0 && (
              <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7, color: "var(--ink-2)" }}>
                {r.reasons.map((why, i) => <li key={i}>{why}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- how long stages actually take, and what that means for live work ---- */
function Timing() {
  const { projects } = useStore();
  const nav = useNavigate();
  const history = stageHistory(projects);
  const live = projects.map((p) => ({ p, f: forecastStage(p, history) })).filter((x) => x.f);
  return (
    <div className="col" style={{ gap: 16 }}>
      <p className="muted reveal" style={{ maxWidth: 660 }}>
        What each stage has actually taken on your finished work, and what that suggests for the projects running now. Evidence from your own record — where there isn't enough of it, it says so rather than dressing a guess as a forecast.
      </p>

      {history.length === 0 ? (
        <div className="panel reveal" style={{ padding: 26, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>
          No completed, dated stages yet — once projects finish stages with start and completion dates, the history builds itself.
        </div>
      ) : (
        <div className="card reveal" style={{ overflow: "hidden" }}>
          <div className="row between" style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)" }}>
            <span className="kicker">Typical stage duration</span><span className="dim" style={{ fontSize: 12 }}>from finished work</span>
          </div>
          {history.map((h, i) => (
            <div key={h.key} className="row between" style={{ padding: "10px 16px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
              <span style={{ fontSize: 13.5 }}>{h.label}</span>
              <span className="row" style={{ gap: 10 }}>
                <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{h.medianDays} days</span>
                <span className="badge" style={{ fontSize: 8.5 }}>{h.samples} project{h.samples === 1 ? "" : "s"}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {live.length > 0 && (
        <>
          <div className="kicker reveal">Running now</div>
          <div className="col" style={{ gap: 10 }}>
            {live.map(({ p, f }) => (
              <div key={p.id} className="card pad reveal">
                <div className="row between wrap" style={{ gap: 10 }}>
                  <span className="row" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</span>
                    <span className="badge" style={{ fontSize: 8.5 }}>{f!.stageLabel}</span>
                  </span>
                  <button className="btn ghost sm" onClick={() => nav(`/studio/${p.id}/stages`)}>Open stages</button>
                </div>
                <div className="row wrap" style={{ gap: 18, marginTop: 8, alignItems: "baseline" }}>
                  <span className="dim" style={{ fontSize: 12.5 }}>{f!.elapsedDays} days in{f!.typicalDays ? ` of a typical ${f!.typicalDays}` : ""}</span>
                  {f!.projectedEnd && <span className="dim" style={{ fontSize: 12.5 }}>on that pace, ending ~{fmtDate(f!.projectedEnd)}</span>}
                  <span className="badge" style={{ fontSize: 8.5, color: f!.confidence === "fair" ? "var(--ok)" : f!.confidence === "low" ? "var(--warn)" : "var(--ink-3)" }}>{f!.confidence === "none" ? "no history" : `${f!.confidence} confidence`}</span>
                </div>
                {f!.typicalDays && <div style={{ marginTop: 8 }}><Meter value={Math.min(100, (f!.elapsedDays / f!.typicalDays) * 100)} color={f!.elapsedDays > f!.typicalDays ? "var(--warn)" : undefined} /></div>}
                <div className="dim" style={{ fontSize: 12, marginTop: 7, lineHeight: 1.55 }}>{f!.note}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---- one address book across every project ---- */
function Contacts() {
  const { projects } = useStore();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const book = contactBook(projects);
  const list = book.filter((c) => !q.trim() || `${c.name} ${c.email ?? ""} ${c.company ?? ""} ${c.roles.join(" ")}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted reveal" style={{ maxWidth: 640 }}>
        Everyone across every project, folded together so a person appears once — with where they turn up and how to reach them.
      </p>
      <div className="row between wrap reveal" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 8, flex: "1 1 260px", minWidth: 220, position: "relative" }}>
          <Icon.brief width={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)", pointerEvents: "none" }} />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" style={{ paddingLeft: 32, width: "100%" }} />
        </div>
        <span className="dim" style={{ fontSize: 12.5 }}>{list.length} of {book.length}</span>
        {list.length > 0 && <button className="btn ghost sm" onClick={() => printTable({
          title: "Contacts", subtitle: STUDIO.name, meta: [`${list.length} people`],
          columns: [{ key: "name", label: "Name" }, { key: "company", label: "Company" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone", width: "110px" }, { key: "roles", label: "Role" }, { key: "projects", label: "Projects" }],
          rows: list.map((c) => ({ name: c.name, company: c.company ?? "", email: c.email ?? "", phone: c.phone ?? "", roles: c.roles.join(", "), projects: c.projects.map((x) => x.code).join(", ") })),
        })}><Icon.report width={13} />Print</button>}
      </div>
      {list.length === 0 ? (
        <div className="panel reveal" style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>Nobody matches.</div>
      ) : (
        <div className="card reveal" style={{ overflow: "hidden" }}>
          {list.map((c, i) => (
            <div key={c.key} style={{ padding: "12px 16px", borderTop: i ? "1px solid var(--line)" : "none" }}>
              <div className="row between wrap" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                  {c.company && <span className="dim" style={{ fontSize: 12 }}>{c.company}</span>}
                  {c.roles.map((r) => <span key={r} className="badge" style={{ fontSize: 8.5 }}>{r}</span>)}
                </span>
                <span className="row wrap" style={{ gap: 10, flexShrink: 0 }}>
                  {c.email && <a className="dim" style={{ fontSize: 12 }} href={`mailto:${c.email}`}>{c.email}</a>}
                  {c.phone && <span className="dim mono" style={{ fontSize: 12 }}>{c.phone}</span>}
                </span>
              </div>
              <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                {c.projects.map((x) => <button key={x.id} className="chip" style={{ fontSize: 10.5 }} onClick={() => nav(`/studio/${x.id}/dashboard`)}>{x.name}</button>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- one message to many clients / consultants ---- */
function BulkMessage() {
  const { projects } = useStore();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ ok: number; fail: number } | null>(null);

  // one row per contactable party across the portfolio
  const people = projects.flatMap((p) => [
    ...(p.client?.email ? [{ id: `c-${p.id}`, name: p.client.name, email: p.client.email, phone: p.client.phone, role: "Client", project: p.name }] : []),
    ...(p.consultants ?? []).filter((c) => c.email).map((c) => ({ id: `k-${p.id}-${c.id}`, name: c.contact || c.firm, email: c.email!, phone: c.phone, role: c.discipline, project: p.name })),
  ]);
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const chosen = people.filter((x) => sel.has(x.id));

  async function send() {
    if (!chosen.length || !subject.trim() || !body.trim()) return;
    setBusy(true); setSent(null);
    let ok = 0, fail = 0;
    for (const person of chosen) {
      const r = await notify({ to: person.email, subject: subject.trim(), heading: subject.trim(), body: body.trim().replace(/\{name\}/g, person.name.split(" ")[0]).replace(/\{project\}/g, person.project) });
      if (r === "delivered") ok++; else fail++;
    }
    setBusy(false); setSent({ ok, fail });
  }

  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted reveal" style={{ maxWidth: 660 }}>
        One message to several clients or consultants at once — a closure notice, a fee-schedule change, a general update. Use <span className="mono">{"{name}"}</span> and <span className="mono">{"{project}"}</span> and each message is personalised.
      </p>
      {people.length === 0 ? (
        <div className="panel reveal" style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>No contacts with an email on file yet.</div>
      ) : (
        <>
          <div className="card reveal" style={{ overflow: "hidden" }}>
            <div className="row between" style={{ padding: "11px 16px", borderBottom: "1px solid var(--line)" }}>
              <span className="kicker">Recipients</span>
              <span className="row" style={{ gap: 8 }}>
                <button className="chip" style={{ fontSize: 11 }} onClick={() => setSel(new Set(people.map((x) => x.id)))}>All {people.length}</button>
                <button className="chip" style={{ fontSize: 11 }} onClick={() => setSel(new Set(people.filter((x) => x.role === "Client").map((x) => x.id)))}>Clients only</button>
                {sel.size > 0 && <button className="chip" style={{ fontSize: 11 }} onClick={() => setSel(new Set())}>Clear</button>}
              </span>
            </div>
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {people.map((x, i) => (
                <label key={x.id} className="row between" style={{ padding: "9px 16px", borderTop: i ? "1px solid var(--line)" : "none", cursor: "pointer", gap: 10 }}>
                  <span className="row" style={{ gap: 10, minWidth: 0 }}>
                    <input type="checkbox" checked={sel.has(x.id)} onChange={() => toggle(x.id)} />
                    <span style={{ fontSize: 13.5 }}>{x.name}</span>
                    <span className="dim" style={{ fontSize: 11.5 }}>{x.email}</span>
                  </span>
                  <span className="row" style={{ gap: 6, flexShrink: 0 }}>
                    <span className="badge" style={{ fontSize: 8.5 }}>{x.role}</span>
                    <span className="dim" style={{ fontSize: 11 }}>{x.project}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="field reveal"><label>Subject</label><input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Studio closure — 24 Dec to 2 Jan" /></div>
          <div className="field reveal"><label>Message</label><textarea className="input" rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder={"Hi {name},\n\nA note about {project}…"} /></div>
          <div className="row between wrap reveal" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 12.5 }}>{chosen.length} recipient{chosen.length === 1 ? "" : "s"} selected{sent ? ` · ${sent.ok} sent${sent.fail ? `, ${sent.fail} failed` : ""}` : ""}</span>
            <button className="btn accent" disabled={busy || !chosen.length || !subject.trim() || !body.trim()} onClick={send}><Icon.send width={15} />{busy ? "Sending…" : `Send to ${chosen.length}`}</button>
          </div>
          <div className="dim reveal" style={{ fontSize: 11.5, lineHeight: 1.6 }}>Each person gets their own email — nobody sees the others. Sends one at a time, so a large list takes a moment.</div>
        </>
      )}
    </div>
  );
}

/* ---- who's loaded, across every project ---- */
const WEEKS = 8;
function mondayOf(d: Date) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }

function Capacity() {
  const { projects, team } = useStore();
  const start = mondayOf(new Date());
  const weeks = Array.from({ length: WEEKS }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i * 7); return d; });
  // every programme task with an owner and dates counts as load in the weeks it spans
  const owners = [...new Set([...team.map((m) => m.name), STUDIO.principal])];
  const grid: Record<string, number[]> = {};
  const detail: Record<string, string[][]> = {};
  for (const o of owners) { grid[o] = new Array(WEEKS).fill(0); detail[o] = Array.from({ length: WEEKS }, () => []); }
  for (const p of projects) {
    for (const t of ((p as any).program ?? [])) {
      const owner = t.owner && grid[t.owner] ? t.owner : null;
      if (!owner || !t.start || !t.end || t.status === "done") continue;
      const s = new Date(t.start), e = new Date(t.end);
      weeks.forEach((w, i) => {
        const wEnd = new Date(w); wEnd.setDate(wEnd.getDate() + 6);
        if (s <= wEnd && e >= w) { grid[owner][i]++; detail[owner][i].push(`${p.name}: ${t.name}`); }
      });
    }
  }
  const busiest = Math.max(1, ...owners.flatMap((o) => grid[o]));
  const load = (n: number) => n === 0 ? "var(--line-2)" : n <= 2 ? "var(--sage)" : n <= 4 ? "var(--warn)" : "var(--danger)";

  return (
    <div className="col" style={{ gap: 16 }}>
      <p className="muted reveal" style={{ maxWidth: 640 }}>
        Who's committed over the next {WEEKS} weeks, from the work programmes across every project. Green is comfortable, amber is full, red is over-committed.
      </p>
      <div className="card reveal" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "12px 14px", borderBottom: "1px solid var(--line)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--ink-3)" }}>Person</th>
              {weeks.map((w, i) => <th key={i} style={{ padding: "12px 6px", borderBottom: "1px solid var(--line)", fontSize: 10, color: "var(--ink-3)", fontWeight: 500, whiteSpace: "nowrap" }}>{w.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</th>)}
            </tr>
          </thead>
          <tbody>
            {owners.map((o) => (
              <tr key={o} style={{ borderBottom: "1px solid var(--line)" }}>
                <td style={{ padding: "10px 14px", fontWeight: 500, whiteSpace: "nowrap" }}>{o}</td>
                {grid[o].map((n, i) => (
                  <td key={i} style={{ padding: "6px 4px", textAlign: "center" }} title={detail[o][i].join("\n") || "Free"}>
                    <div style={{ height: 26, borderRadius: 6, background: load(n), opacity: n === 0 ? 0.35 : 0.25 + Math.min(0.75, (n / busiest) * 0.75), display: "grid", placeItems: "center", fontSize: 11, fontWeight: 600, color: n > 2 ? "#fff" : "var(--ink-2)" }}>{n || ""}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dim reveal" style={{ fontSize: 11.5 }}>Counts live programme tasks assigned to each person in that week. Hover a cell to see what they are.</div>
    </div>
  );
}

/* ---- the studio's institutional memory ---- */
function corpusOf(projects: Project[]): string {
  const L: string[] = [];
  for (const p of projects) {
    L.push(`## ${p.name} (${p.code}) — ${p.type}${p.location ? `, ${p.location}` : ""}, budget ${p.budget} ${p.currency}, started ${p.createdOn}`);
    const dels = p.deliverables ?? [];
    if (dels.length) L.push(`Sheets: ${dels.slice(0, 120).map((d) => `${d.code} ${d.title}`).join("; ")}`);
    const spaces = (p.brief as any)?.spaces ?? [];
    if (spaces.length) L.push(`Accommodation: ${spaces.map((s: any) => `${s.name} ×${s.qty} @${s.area}`).join("; ")}`);
    const sections = (p.brief?.sections ?? []).filter((s) => s.body?.trim());
    for (const s of sections.slice(0, 6)) L.push(`Brief — ${s.title}: ${s.body.slice(0, 400)}`);
    if (p.billing) L.push(`Fee: ${p.billing.contractSum} ${p.billing.currency}${p.billing.feePct ? ` (${p.billing.feePct}% of construction)` : ""}.`);
    if (p.boq?.length) L.push(`Quantities: ${p.boq.slice(0, 30).map((b) => `${b.description} ${b.qty}${b.unit}`).join("; ")}`);
    for (const r of (p.reviews ?? [])) for (const sh of r.sheets) for (const m of sh.remarks) if (m.response) L.push(`Decision on ${sh.code}: asked "${m.body.slice(0, 160)}" → did "${m.response.body.slice(0, 200)}"`);
    for (const rf of (p.rfis ?? [])) if (rf.answer) L.push(`RFI ${rf.ref} (${p.code}) ${rf.subject}: ${rf.answer.slice(0, 240)}`);
    for (const v of (p.variations ?? [])) L.push(`Variation ${v.ref} (${p.code}): ${v.title} — ${v.amount} [${v.status}]`);
  }
  return L.join("\n");
}

const KB_SUGGESTIONS = ["How have we detailed washrooms before?", "What did we charge for a meetinghouse?", "Which projects used a standing-seam roof?", "What change requests come up most often?"];

function KnowledgeBase() {
  const { projects } = useStore();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [thread, setThread] = useState<{ q: string; a: string }[]>([]);

  async function ask(question: string) {
    const query = question.trim();
    if (!query || busy) return;
    setBusy(true); setErr(null); setQ("");
    const res = await knowledgeAsk(query, corpusOf(projects));
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The knowledge base isn't switched on (needs an AI key)." : "Couldn't reach the knowledge base."); return; }
    setThread((t) => [...t, { q: query, a: res.answer ?? "" }]);
  }

  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted reveal" style={{ maxWidth: 640 }}>
        Ask across every project the studio has done — details, specs, fees, decisions and how change requests were resolved. Answers cite the project they came from, and say so when the record doesn't hold the answer.
      </p>
      {thread.length === 0 && <div className="row wrap reveal" style={{ gap: 6 }}>{KB_SUGGESTIONS.map((s) => <button key={s} className="chip" style={{ fontSize: 11.5 }} onClick={() => ask(s)}>{s}</button>)}</div>}
      {thread.length > 0 && (
        <div className="col reveal" style={{ gap: 12 }}>
          {thread.map((m, i) => (
            <div key={i} className="col" style={{ gap: 6 }}>
              <div className="row" style={{ gap: 8 }}><span className="badge" style={{ fontSize: 9 }}>You</span><span style={{ fontSize: 13.5, fontWeight: 500 }}>{m.q}</span></div>
              <div className="panel" style={{ padding: "11px 14px", whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.6 }}>{m.a}</div>
            </div>
          ))}
        </div>
      )}
      {busy && <div className="row" style={{ gap: 8, color: "var(--ink-3)", fontSize: 13 }}><span className="dot" />Searching the studio record…</div>}
      {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
      <div className="row reveal" style={{ gap: 8 }}>
        <input className="input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask(q)} placeholder="How did we handle…?" />
        <button className="btn accent" disabled={busy || !q.trim()} onClick={() => ask(q)}><Icon.send width={15} /></button>
      </div>
      <div className="dim reveal" style={{ fontSize: 11.5 }}>Reading {projects.length} project{projects.length === 1 ? "" : "s"}.</div>
    </div>
  );
}

/* ---- saved project skeletons ---- */
function Templates() {
  const { projects, templates = [] } = useStore();
  const session = useSession();
  const [save, setSave] = useState(false);
  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="row between wrap reveal" style={{ gap: 10 }}>
        <p className="muted" style={{ maxWidth: 620, margin: 0 }}>Save a project's skeleton — its drawing register, fee model and brief structure — and start the next one from it. Ideal for repeat typologies.</p>
        <button className="btn accent" onClick={() => setSave(true)}><Icon.plus width={15} />Save a template</button>
      </div>
      {templates.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}><div className="kicker">No templates yet</div><p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>Save one from a project you'd happily repeat — the register, fee stages and brief headings carry over to every new project you start from it.</p></div>
      ) : (
        <div className="col" style={{ gap: 10 }}>
          {templates.map((t) => (
            <div key={t.id} className="card pad reveal row between wrap" style={{ gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{t.name}</div>
                <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>{[t.type, `${t.deliverables.length} sheets`, t.milestones?.length ? `${t.milestones.length} fee stages` : null, `saved ${fmtDate(t.createdOn)} by ${t.createdBy}`].filter(Boolean).join(" · ")}</div>
                {t.note && <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>{t.note}</div>}
              </div>
              <button className="iconbtn" style={{ width: 30, height: 30 }} title="Remove" onClick={() => { if (confirm(`Remove the template “${t.name}”?`)) actions.removeTemplate(t.id); }}><Icon.close width={13} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="dim reveal" style={{ fontSize: 11.5 }}>Pick a template when you create a new project.</div>
      {save && <SaveTemplateModal projects={projects} by={session?.name ?? STUDIO.principal} onClose={() => setSave(false)} />}
    </div>
  );
}

function SaveTemplateModal({ projects, by, onClose }: { projects: Project[]; by: string; onClose: () => void }) {
  const [pid, setPid] = useState(projects[0]?.id ?? "");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const p = projects.find((x) => x.id === pid);
  return (
    <Modal open onClose={onClose} title="Save a project template">
      <div className="col" style={{ gap: 14 }}>
        <div className="field"><label>From project</label>
          <select className="select" value={pid} onChange={(e) => setPid(e.target.value)}>{projects.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.code}</option>)}</select>
        </div>
        {p && <div className="panel" style={{ padding: "10px 13px", fontSize: 12.5, lineHeight: 1.6 }}>Captures <b>{(p.deliverables ?? []).length}</b> register sheets, {(p.billing?.milestones ?? []).length} fee stages, {(p.brief?.sections ?? []).length} brief headings{p.estimate ? ", and the fee configuration" : ""}. No client data or drawings are copied.</div>}
        <div className="field"><label>Template name</label><input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={p ? `${p.type} — standard set` : "Template name"} /></div>
        <div className="field"><label>Note <span className="dim">(optional)</span></label><input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="When to use this one." /></div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!pid} onClick={() => { actions.saveTemplate(pid, name || `${p?.type ?? "Project"} template`, by, note); onClose(); }}><Icon.check width={15} />Save template</button>
        </div>
      </div>
    </Modal>
  );
}

/* ---- workspace backup ---- */
function Backup() {
  const isAdmin = useIsAdmin();
  const { projects } = useStore();
  const [done, setDone] = useState<string | null>(null);

  function download(data: unknown, filename: string) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    a.download = filename; a.click();
    setDone(filename); setTimeout(() => setDone(null), 4000);
  }
  const stamp = new Date().toISOString().slice(0, 10);
  const size = (() => { try { return (JSON.stringify(getState()).length / 1048576).toFixed(2); } catch { return "?"; } })();

  return (
    <div className="col" style={{ gap: 14 }}>
      <p className="muted reveal" style={{ maxWidth: 640 }}>
        A complete, portable copy of the workspace — every project, drawing register, invoice and record — as a JSON file you keep. Take one before any big change, and keep one off-platform.
      </p>
      <div className="card pad reveal">
        <div className="row between wrap" style={{ gap: 12 }}>
          <div>
            <div className="kicker">Whole workspace</div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>{projects.length} project{projects.length === 1 ? "" : "s"} · about {size} MB</div>
          </div>
          <button className="btn accent" onClick={() => download(getState(), `atelier-workspace-${stamp}.json`)}><Icon.download width={15} />Download backup</button>
        </div>
      </div>
      <div className="card pad reveal">
        <div className="kicker" style={{ marginBottom: 10 }}>A single project</div>
        <div className="col" style={{ gap: 8 }}>
          {projects.map((p) => (
            <div key={p.id} className="row between" style={{ gap: 10 }}>
              <span style={{ fontSize: 13.5 }}>{p.name} <span className="dim">· {p.code}</span></span>
              <button className="btn ghost sm" onClick={() => download(p, `${(p.code || p.name).replace(/[^\w]+/g, "_")}-${stamp}.json`)}><Icon.download width={13} />Export</button>
            </div>
          ))}
        </div>
      </div>
      {done && <div className="badge ok reveal" style={{ alignSelf: "flex-start" }}><Icon.check width={11} />Saved {done}</div>}
      {isAdmin && <div className="dim reveal" style={{ fontSize: 11.5, lineHeight: 1.6 }}>Restoring from a backup is deliberately not a button — it would overwrite live data for everyone. Send the file back to your developer if you ever need a restore.</div>}
    </div>
  );
}
